/**
 * Wires `transactions.model.ts` to the Transaction Centre's reads and write.
 *
 * Calls `fetchRegistrationFees` for the subject, and `fetchGalleryPlacements`
 * and `fetchGallerySettlements` only to be counted — each swallows its
 * failure and leaves a zero, since the ledger is readable without a count.
 * The one write is `recordFeePaid`, the firm's word that an invoice was
 * settled, followed by a re-read rather than a patched row.
 *
 * The state the model cannot hold is which fee is armed for the two-press
 * recording, which is being written, and the reload counter.
 */

import { useEffect, useState } from "react";
import type {
  BillingContactResponse,
  OrgMember,
  PlacedWork,
  RegistrationFee,
  Settlement,
} from "@ondo/shared";

import {
  fetchBillingContact,
  fetchGalleryPlacements,
  fetchGallerySettlements,
  fetchOrgProfile,
  fetchRegistrationFees,
  recordFeePaid,
  setBillingContact,
} from "../lib/api.ts";
import {
  describeTransactionsError,
  toBillingContactView,
  toTransactionsView,
} from "./transactions.model.ts";
import type { BillingContactView, TransactionsView } from "./transactions.model.ts";

export interface TransactionsController {
  view: TransactionsView;
  /** Who the firm's fee notices go to — OND-93. */
  billing: BillingContactView;
  /** Names somebody else, or clears the choice so the default applies again. */
  chooseBillingContact: (contactOndoId: string | null) => void;
  changingBilling: boolean;
  /** First press. Arms one row and disarms any other. */
  askToRecord: (feeId: string) => void;
  cancelRecord: () => void;
  /** Second press. Records the firm's word, with an optional reference. */
  record: (feeId: string, note: string) => void;
  confirmingId: string | null;
  working: string | null;
}

/**
 * The Transaction Centre: what a sale produces, in one place.
 *
 * THREE READS, and two of them are counts. The fees are the subject — nothing
 * else lists them — and the awaiting-buyer sales and due settlements are read
 * only to be counted, because Sold Works and Settlements are where each is
 * acted on. A failure on either count leaves a zero rather than taking the
 * screen down: a count is supporting evidence, and the ledger is readable
 * without it.
 *
 * ONE WRITE, two presses. Recording a fee as paid is the firm's assertion —
 * ONDO takes no money — and it is append-once in SQL, so it takes the same
 * two-press shape ending a sale does, with the sentence between the presses
 * saying what is actually recorded.
 *
 * RE-READ AFTER THE WRITE rather than patching a row, the rule every money
 * screen here keeps: the ledger is the only copy of what is true.
 *
 * @param workBase '/gallery/ONDO-6624/work' — where a row links to.
 */
export function useTransactionsController(
  workBase: string,
  firmOndoId: string,
): TransactionsController {
  const [fees, setFees] = useState<RegistrationFee[] | null>(null);
  const [placements, setPlacements] = useState<PlacedWork[] | null>(null);
  const [settlements, setSettlements] = useState<Settlement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  // OND-93's two reads, kept apart from the ledger's error channel on purpose:
  // a firm whose roster cannot be read still has a readable ledger, and a
  // billing-contact failure must not take the fee list down with it.
  const [contact, setContact] = useState<BillingContactResponse | null>(null);
  const [members, setMembers] = useState<OrgMember[] | null>(null);
  const [billingError, setBillingError] = useState<string | null>(null);
  const [changingBilling, setChangingBilling] = useState(false);

  useEffect(() => {
    let active = true;

    fetchRegistrationFees()
      .then((found) => {
        if (active) setFees(found);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeTransactionsError(cause));
      });

    // The two counts. Swallowed on failure, deliberately — see the header.
    void fetchGalleryPlacements()
      .then((found) => {
        if (active) setPlacements(found);
      })
      .catch(() => undefined);
    void fetchGallerySettlements()
      .then((found) => {
        if (active) setSettlements(found);
      })
      .catch(() => undefined);

    fetchBillingContact(firmOndoId)
      .then((found) => {
        if (active) setContact(found);
      })
      .catch((cause: unknown) => {
        if (active) setBillingError(describeTransactionsError(cause));
      });

    // The roster, so a director picks a colleague rather than typing an id.
    // Swallowed on failure like the two counts: the panel still names who is
    // told, which is the half everybody is owed, and only the CONTROL needs
    // the list. A seat that cannot read the roster is also a seat that cannot
    // change the contact.
    void fetchOrgProfile(firmOndoId)
      .then((profile) => {
        if (active) setMembers(profile.members);
      })
      .catch(() => undefined);

    return () => {
      active = false;
    };
  }, [nonce, firmOndoId]);

  return {
    view: toTransactionsView({ fees, placements, settlements, error, workBase }),
    billing: toBillingContactView({ contact, members, error: billingError }),
    changingBilling,
    confirmingId,
    working,

    chooseBillingContact: (contactOndoId) => {
      setChangingBilling(true);
      setBillingError(null);

      setBillingContact(firmOndoId, contactOndoId)
        // REPLACED WITH WHAT THE SERVER ANSWERED rather than with what was
        // asked for, because clearing the choice does not mean "nobody" — the
        // default answers again, and only the door knows who that is.
        .then((found) => setContact(found))
        .catch((cause: unknown) => setBillingError(describeTransactionsError(cause)))
        .finally(() => setChangingBilling(false));
    },

    askToRecord: (feeId) => {
      setError(null);
      setConfirmingId(feeId);
    },

    cancelRecord: () => setConfirmingId(null),

    record: (feeId, note) => {
      setWorking(feeId);
      setError(null);

      recordFeePaid(feeId, { note: note.trim() === "" ? null : note })
        .then(() => {
          setConfirmingId(null);
          setNonce((n) => n + 1);
        })
        .catch((cause: unknown) => setError(describeTransactionsError(cause)))
        .finally(() => setWorking(null));
    },
  };
}
